import { action, makeObservable, observable, IObservableArray } from 'mobx';
import { Injectable } from './service.base';
import { AxiosResponse } from 'axios';
import moment from 'moment';
import { api } from '~/tools/api/api';
import { EApiUrl } from '~/tools/api/enums';
import { ILinkAccount, IOtpData } from '~/views/types';
import { ModelCreditCard } from '~/models/model.credit.card';
import { ModelPurchaseHistoryItem } from '~/models/model.purchase.history';
import { ModelUser } from '~/models/model.user';
import { VerificationCodeDialogService } from './service.verification.code.dialog';
import { extractServerError } from '~/tools/error';

@Injectable()
export class AccountService {
    @observable public isLinkAccountBtnDisabled = true;
    @observable public isLinkUnityEmailInvalid = false;
    @observable public isDateOfBirthInvalid = false;
    @observable public showLoading = false;
    @observable public historyNextCursor = '';
    @observable public showLoadingHistory = false;
    @observable public transactionsExists = false;
    @observable public lastError: { description: string; code?: string } = { description: '', code: undefined };

    private linkAccount: ILinkAccount = {};
    public creditCards: IObservableArray<ModelCreditCard> = observable.array([]);
    public purchaseHistory: IObservableArray<ModelPurchaseHistoryItem> = observable.array([]);
    private otpData: IOtpData;

    constructor() {
        makeObservable(this);
    }

    @action
    handleUnityEmailBlur = (event: { target: { value: string } }) => {
        this.clearLastError();
        if (event.target.value) {
            if (
                !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(event.target.value) &&
                !/^\d+$/i.test(event.target.value)
            ) {
                this.isLinkUnityEmailInvalid = true;
                this.linkAccount.unityAccountEmail = null;
            } else {
                this.isLinkUnityEmailInvalid = false;
                this.linkAccount.unityAccountEmail = event.target.value;
            }
        } else {
            this.linkAccount.unityAccountEmail = null;
        }
        this.validateLinkAccountAllFields();
    };

    @action
    validateLinkAccountAllFields = () => {
        this.isLinkAccountBtnDisabled = (!this.linkAccount.unityAccountEmail ||
            !this.linkAccount.dateBirth) as unknown as boolean;
    };

    @action
    handleDateOfBirthChange = (event: { target: { value: string } }) => {
        if (event.target.value) {
            const dateArr: Array<string> = event.target.value.split('/');
            if (dateArr.length < 3) {
                this.isDateOfBirthInvalid = true;
            } else {
                const month: number = dateArr[0] as unknown as number;
                const day: number = dateArr[1] as unknown as number;
                const year: number = dateArr[2] as unknown as number;

                const dateFormat = 'YYYY-MM-DD';
                const date = `${year}-${month}-${day}`;
                const toDateFormat = moment(new Date(date)).format(dateFormat);

                if (moment(toDateFormat, dateFormat, true).isValid()) {
                    const currentYear: number = new Date().getFullYear();
                    const allowYear: number = currentYear - 18;
                    const hunderYearAgo: number = currentYear - 100;

                    if (year > allowYear || year < hunderYearAgo) {
                        this.isDateOfBirthInvalid = true;
                    } else {
                        this.linkAccount.dateBirth = event.target.value;
                        this.isDateOfBirthInvalid = false;
                    }
                } else {
                    this.isDateOfBirthInvalid = true;
                }
            }
        } else {
            this.linkAccount.dateBirth = null;
        }
        this.validateLinkAccountAllFields();
    };

    @action
    handleDateOfBirthLinkFocus = () => {
        this.isDateOfBirthInvalid = false;
    };

    @action
    linkUnityAccount = async (currentUser: ModelUser | null, verificationCode: string): Promise<boolean> => {
        this.clearLastError();
        let verifyResult = false;
        this.setShowLoading(true);

        try {
            const data = {
                otp: verificationCode,
                transactionId: this.otpData.transactionId,
                unityUserId: this.otpData.unityUserId,
            };

            const response: AxiosResponse = await api.post(
                EApiUrl.unityOtp,
                data,
                {
                    headers: {
                        Authorization: currentUser ? currentUser.token : '',
                    },
                },
                true,
            );
            if (response.status === 200 && !response.data?.error) {
                verifyResult = true;
            }
        } catch (error: any) {
            this.setLastError(extractServerError(error?.response?.data ?? error));
        } finally {
            this.setShowLoading(false);
        }

        return verifyResult;
    };

    sendVerificationCode = async (token: string | undefined): Promise<boolean> => {
        this.clearLastError();
        let sendResult = false;

        this.setShowLoading(true);
        const email =
            this.linkAccount.unityAccountEmail && this.linkAccount.unityAccountEmail.includes('@')
                ? this.linkAccount.unityAccountEmail
                : null;
        const accountNumber =
            this.linkAccount.unityAccountEmail && !this.linkAccount.unityAccountEmail.includes('@')
                ? this.linkAccount.unityAccountEmail
                : null;
        const dateArr: Array<string> = this.linkAccount.dateBirth ? this.linkAccount.dateBirth.split('/') : [];
        const month: number = dateArr[0] as unknown as number;
        const day: number = dateArr[1] as unknown as number;
        const year: number = dateArr[2] as unknown as number;

        const dateOfBirth = `${year}-${month}-${day}`;
        const url = `${EApiUrl.unitySignIn}`;
        const data = { accountNumber, email, dateOfBirth };

        try {
            const response: AxiosResponse = await api.post(
                url,
                data,
                {
                    headers: {
                        Authorization: token,
                    },
                },
                true,
            );

            if (response.status === 200) {
                sendResult = true;
                this.otpData = response.data;
            }
        } catch (error: any) {
            this.setLastError(extractServerError(error?.response?.data ?? error));
        } finally {
            this.setShowLoading(false);
        }

        return sendResult;
    };

    @action
    setShowLoading = (value: boolean) => {
        this.showLoading = value;
    };

    @action
    setCreditCards = (creditCards: any) => {
        this.creditCards.replace(creditCards);
    };

    @action
    openVerificationCodeDialog = async (
        verificationCodeDialogSrv: VerificationCodeDialogService,
        token: string | undefined,
    ) => {
        const sendResult: boolean = await this.sendVerificationCode(token);

        if (sendResult) {
            verificationCodeDialogSrv.handleOpen();
        }
    };

    @action
    onLinkAccountKeyUp = (
        e: any,
        verificationCodeDialogSrv: VerificationCodeDialogService,
        token: string | undefined,
    ) => {
        if (e.keyCode === 13 && !this.isLinkAccountBtnDisabled) {
            this.openVerificationCodeDialog(verificationCodeDialogSrv, token);
        }
    };

    @action
    getPurchaseHistory = async (authService: any, loadMore = false) => {
        this.clearLastError();
        this.setShowLoadingHistory(true);
        try {
            const purchaseHistoryUrl =
                loadMore && this.historyNextCursor
                    ? `${EApiUrl.purchaseHistory}?next=${this.historyNextCursor}`
                    : EApiUrl.purchaseHistory;

            const response: AxiosResponse = await api.get<ModelPurchaseHistoryItem[]>(purchaseHistoryUrl, {
                headers: {
                    Authorization: authService.currentUser.token,
                },
            });
            if (response.status === 200) {
                const transactions = [];
                for (const item of response.data.items) {
                    transactions.push({
                        transactionId: item.id,
                        date: this.formatDateToLocal(item.date),
                        transactionType: item.type,
                        description: item.description,
                        freeplay: item.freePlay,
                        dollarPackage: item.wdollar,
                        amount: item.amount,
                        status: item.status,
                        statusClassName: item.status ? item.status.toLowerCase() : '',
                    });
                }

                this.transactionsExists = transactions.length > 0;
                this.setHistoryNextCursor(response.data.nextCursor);

                this.setPurchaseHistory(transactions, loadMore);
            }
        } catch (error: any) {
            if (error.response && error.response.status && error.response.status === 403) {
                authService.setLastError(extractServerError(error?.response?.data ?? error));
                authService.setShowForbiddenPopup(true);
            } else {
                this.setLastError(extractServerError(error?.response?.data ?? error));
            }
        }

        this.setShowLoadingHistory(false);
    };

    @action
    setPurchaseHistory = (transactions: any, loadMore: boolean) => {
        if (loadMore) {
            const allTransactions = [...this.purchaseHistory, ...transactions];
            this.purchaseHistory.replace(allTransactions);
        } else {
            this.purchaseHistory.replace(transactions);
        }
    };

    @action
    setHistoryNextCursor = (value: string) => {
        this.historyNextCursor = value;
    };

    formatDateToLocal = (date: string): string => {
        const d = new Date(date);
        const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
        // Remove all commas and extra spaces
        const formattedDate = d.toLocaleDateString('en-GB', options).replace(/,/g, '').replace(/\s+/g, ' ').trim();

        return formattedDate;
    };

    @action
    setShowLoadingHistory = (value: boolean) => {
        this.showLoadingHistory = value;
    };

    @action
    setLastError = (err: { description: string; code?: string }) => {
        this.lastError = err;
    };

    @action
    clearLastError = () => {
        this.lastError = { description: '', code: undefined };
    };
}
